import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, BarChart3, CheckCircle2, FileText, Shield, Users, XCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { api, fmtUSD } from '@/lib/api';

export default function AdminPage() {
  const [stats, setStats] = useState({});
  const [verifications, setVerifications] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [users, setUsers] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selReason, setSelReason] = useState('');

  const loadAll = async () => {
    const [s, v, d, u, l] = await Promise.all([
      api.get('/admin/stats'), api.get('/admin/verifications/pending'),
      api.get('/admin/disputes'), api.get('/admin/users?limit=50'),
      api.get('/admin/ledger?limit=30'),
    ]);
    setStats(s.data); setVerifications(v.data.items || []);
    setDisputes(d.data.items || []); setUsers(u.data.items || []);
    setLedger(l.data.items || []);
  };
  useEffect(() => { loadAll(); }, []);

  const approveVerif = async (id) => { try { await api.post(`/admin/verifications/${id}/approve`); toast.success('Approuvé'); loadAll(); } catch (e) { toast.error('Erreur'); } };
  const rejectVerif = async (id) => { try { await api.post(`/admin/verifications/${id}/reject?reason=${encodeURIComponent(selReason || 'Document non conforme')}`); toast.success('Refusé'); loadAll(); } catch { toast.error('Erreur'); } };
  const refundDispute = async (pid) => { try { await api.post(`/payments/${pid}/admin-resolve-refund`); toast.success('Remboursé'); loadAll(); } catch { toast.error('Erreur'); } };
  const releaseDispute = async (pid) => { try { await api.post(`/payments/${pid}/admin-resolve-release`); toast.success('Libéré au jobeur'); loadAll(); } catch { toast.error('Erreur'); } };
  const banUser = async (uid, banned) => { try { await api.post(`/admin/users/${uid}/${banned ? 'unban' : 'ban'}`); toast.success(banned ? 'Débanni' : 'Banni'); loadAll(); } catch { toast.error('Erreur'); } };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="admin-page">
      <div className="flex items-center gap-2">
        <Shield size={22} className="text-[#1677F2]" />
        <h1 className="text-2xl md:text-3xl font-bold">Administration Job Kongo</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <S title="Utilisateurs" v={stats.users_total} />
        <S title="Jobeurs" v={stats.jobeurs} />
        <S title="Clients" v={stats.employeurs} />
        <S title="Pièces en attente" v={stats.verifications_pending} accent />
        <S title="Litiges ouverts" v={stats.disputes_open} accent />
        <S title="Revenue plateforme" v={fmtUSD(stats.platform_revenue_cents)} />
      </div>

      <Tabs defaultValue="verifications">
        <TabsList>
          <TabsTrigger value="verifications" data-testid="admin-tab-verif"><Shield size={14} className="mr-1" /> Vérifications ({verifications.length})</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes"><AlertTriangle size={14} className="mr-1" /> Litiges ({disputes.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={14} className="mr-1" /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="admin-tab-ledger"><BarChart3 size={14} className="mr-1" /> Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="verifications" className="mt-4">
          {verifications.length === 0 ? (
            <Card className="p-6 text-center text-slate-600">Aucune vérification en attente.</Card>
          ) : (
            <div className="space-y-2">
              {verifications.map((v) => (
                <Card key={v.id} className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-sm">User: {v.user_id.slice(0, 8)}…</p>
                      <p className="text-xs text-slate-600">Type: {v.doc_type} · {new Date(v.created_at).toLocaleString('fr-FR')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" onClick={() => setSelected(v)} data-testid={`admin-verif-view-${v.id}`}><Eye size={14} /> Voir</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Vérification {v.doc_type}</DialogTitle>
                            <DialogDescription>User {v.user_id}</DialogDescription>
                          </DialogHeader>
                          {selected && (
                            <div className="space-y-3">
                              {selected.doc_front && <div><p className="text-xs font-semibold">Recto</p><img src={selected.doc_front} alt="recto" className="w-full max-h-80 object-contain rounded border border-[#E2E8F0]" /></div>}
                              {selected.doc_back && <div><p className="text-xs font-semibold">Verso</p><img src={selected.doc_back} alt="verso" className="w-full max-h-80 object-contain rounded border border-[#E2E8F0]" /></div>}
                              {selected.selfie && <div><p className="text-xs font-semibold">Selfie</p><img src={selected.selfie} alt="selfie" className="w-full max-h-80 object-contain rounded border border-[#E2E8F0]" /></div>}
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" onClick={() => approveVerif(v.id)} className="bg-[#1F9D63] hover:bg-[#16764A]" data-testid={`admin-verif-approve-${v.id}`}>
                        <CheckCircle2 size={14} /> Approuver
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => rejectVerif(v.id)} className="text-[#CE1021] border-[#FFD7DB]" data-testid={`admin-verif-reject-${v.id}`}>
                        <XCircle size={14} /> Refuser
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="disputes" className="mt-4">
          {disputes.length === 0 ? (
            <Card className="p-6 text-center text-slate-600">Aucun litige en cours.</Card>
          ) : (
            <div className="space-y-2">
              {disputes.map((p) => (
                <Card key={p.id} className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-sm">Payment {p.id.slice(0, 8)}…</p>
                      <p className="text-xs text-slate-600">Total: {fmtUSD(p.breakdown?.client_total)} · Méthode: {p.payment_method}</p>
                      {p.dispute_reason && <p className="text-xs text-[#A10B18] mt-1">Raison: {p.dispute_reason}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => refundDispute(p.id)} variant="outline" className="text-[#CE1021] border-[#FFD7DB]" data-testid={`admin-dispute-refund-${p.id}`}>Rembourser client</Button>
                      <Button size="sm" onClick={() => releaseDispute(p.id)} className="bg-[#1F9D63] hover:bg-[#16764A]" data-testid={`admin-dispute-release-${p.id}`}>Libérer au jobeur</Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F7FAFF] border-b border-[#E2E8F0]">
                <tr>
                  <th className="text-left p-3">Nom</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Rôle</th>
                  <th className="text-left p-3">Vérif</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#E2E8F0]">
                    <td className="p-3 font-medium">{u.first_name} {u.last_name}</td>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3 capitalize">{u.role}</td>
                    <td className="p-3">
                      {u.phone_verified && '📱'}{u.id_verified && '🛡️'}{u.trusted_jobeur && '⭐'}
                    </td>
                    <td className="p-3">
                      <Button size="sm" variant="outline" onClick={() => banUser(u.id, u.banned)} className={u.banned ? '' : 'text-[#CE1021] border-[#FFD7DB]'} data-testid={`admin-user-ban-${u.id}`}>
                        {u.banned ? 'Débannir' : 'Bannir'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F7FAFF] border-b border-[#E2E8F0]">
                <tr>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Montant</th>
                  <th className="text-left p-3">Paiement</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((e) => (
                  <tr key={e.id} className="border-b border-[#E2E8F0]">
                    <td className="p-3">{e.entry_type}</td>
                    <td className="p-3 font-mono-num">{fmtUSD(e.amount_cents)}</td>
                    <td className="p-3 text-xs text-slate-500">{e.payment_id.slice(0, 8)}…</td>
                    <td className="p-3 text-xs">{new Date(e.created_at).toLocaleString('fr-FR')}</td>
                    <td className="p-3 text-xs text-slate-600">{e.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const S = ({ title, v, accent }) => (
  <Card className="p-3">
    <p className="text-xs text-slate-600">{title}</p>
    <p className={`text-xl md:text-2xl font-bold font-mono-num ${accent ? 'text-[#A10B18]' : 'text-[#0B1220]'}`}>{v ?? '—'}</p>
  </Card>
);
